import React, { useMemo } from 'react';
import { clsx } from 'clsx';
import { PercentBadgeIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import type { TradeItem } from '@/types';
import { aggregateByCurrency } from '@/utils/trades/currency-totals';
import { formatCurrencyExact } from '@/utils/trades/mtm-calculations';
import { useCurrencyDisplayStore } from '@/stores';
import { Z_INDEX } from '@/utils/z-index';
import {
  useElementHeight,
  useShellInsets,
  SHELL_GUTTER_PX,
  SCROLL_TOP_BUTTON_LANE_PX,
} from '@/hooks/common';
import { FAB_BOTTOM_OFFSET_PX } from './fab-geometry';

interface BulkActionBarProps {
  /** The selected trades (>1 — the single-select FAB handles exactly one). */
  selectedTrades: TradeItem[];
  /** Rows in the current filtered view (drives "Select all in view"). */
  visibleCount: number;
  allVisibleSelected: boolean;
  onSelectAllVisible: () => void;
  onReRate: () => void;
  onClear: () => void;
  /** Re-rate is a trade mutation — hidden without the create_trades capability. */
  canTrade: boolean;
  /** LR-600: rendered height (px) — see FloatingActionBar's prop of the same name. */
  onHeightChange?: (px: number) => void;
  /** LR-600: see FloatingActionBar's prop of the same name. */
  scrollTopVisible?: boolean;
}

const btn =
  'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border transition-colors cursor-pointer whitespace-nowrap';
const btnWhite = 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400';
const btnBlue = 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:border-blue-700';

/**
 * LR-539: the multi-select action bar. Shown when >1 rows are ticked (the
 * single-row FloatingActionBar renders at exactly 1). Counts the selection,
 * shows its ≈ base-currency value, and hosts the bulk Re-rate entry point plus
 * Select-all-in-view / Clear. Mirrors the FAB's floating geometry.
 */
export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedTrades,
  visibleCount,
  allVisibleSelected,
  onSelectAllVisible,
  onReRate,
  onClear,
  canTrade,
  onHeightChange,
  scrollTopVisible = false,
}) => {
  const baseCurrency = useCurrencyDisplayStore((s) => s.baseCurrency);
  const n = selectedTrades.length;
  // Hooks stay above the early return (rules of hooks) — the bar is conditional.
  // Gutter grows by the scroll-to-top button's lane while it is up — see the
  // FloatingActionBar comment for why it is added to both sides.
  const insets = useShellInsets(
    SHELL_GUTTER_PX + (scrollTopVisible ? SCROLL_TOP_BUTTON_LANE_PX : 0),
  );
  const measureRef = useElementHeight(onHeightChange);

  const value = useMemo(() => aggregateByCurrency(selectedTrades, undefined, baseCurrency), [selectedTrades, baseCurrency]);

  if (n < 2) return null;

  return (
    <div
      ref={measureRef}
      // LR-600: shell insets centre the bar in the content band and clamp it to
      // that band — see the FloatingActionBar comment for the mechanics.
      style={{
        zIndex: Z_INDEX.FLOATING_ACTION_BAR,
        bottom: FAB_BOTTOM_OFFSET_PX,
        left: insets.left,
        right: insets.right,
      }}
      className={clsx(
        'fixed mx-auto w-fit',
        'flex flex-wrap items-center gap-4 rounded-xl bg-white px-5 py-3',
        'shadow-2xl shadow-blue-900/30 border border-gray-200',
        'motion-safe:transition-[left,right] motion-safe:duration-300 motion-safe:ease-in-out',
        'motion-safe:animate-[slideInUp_200ms_ease-out]',
      )}
    >
      <span className="text-sm font-semibold text-gray-900">
        {n} selected
      </span>
      <span className="text-sm text-gray-500">
        Σ value{' '}
        <span className="font-mono font-semibold text-gray-800">
          ≈ {formatCurrencyExact(value.equiv, value.base)}
        </span>
        {value.approxCurrencies.length > 0 && (
          <span className="ml-1 text-amber-600" title={`No FX for ${value.approxCurrencies.join(', ')} — excluded from Σ`}>⚠</span>
        )}
      </span>

      <div className="h-6 w-px bg-gray-200" />

      {!allVisibleSelected && visibleCount > n && (
        <button type="button" className={clsx(btn, btnWhite)} onClick={onSelectAllVisible}>
          <CheckIcon className="h-4 w-4" />
          Select all {visibleCount}
        </button>
      )}

      {canTrade && (
        <button type="button" className={clsx(btn, btnBlue)} onClick={onReRate}>
          <PercentBadgeIcon className="h-4 w-4" />
          Re-rate {n}…
        </button>
      )}

      <button
        type="button"
        className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
        onClick={onClear}
        aria-label="Clear selection"
      >
        <XMarkIcon className="h-5 w-5" />
      </button>
    </div>
  );
};
